import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';

import { EverywhereTest, TESTS } from './data';
import { everywhereProductSlug } from './helpers';

/** The store product whose master variant price is the clinician review fee. */
export const EVERYWHERE_REVIEW_FEE_SLUG = 'everywhere-clinician-review';

export type EverywherePricing = {
  /** Spree lab price, or null when the test is not sold in this environment. */
  labPriceFor: (test: EverywhereTest) => number | null;
  /** Clinician review fee, or null when the store has no review product. */
  reviewFee: number | null;
  /** Catalog tests sold in this environment, in catalog order. */
  availableTests: EverywhereTest[];
};

type PriceNode = {
  slug?: unknown;
  variants?: unknown;
};

function masterPrice(node: PriceNode): number | null {
  if (!Array.isArray(node.variants)) return null;

  const master = node.variants.find(
    (variant): variant is { price: unknown } =>
      typeof variant === 'object' &&
      variant !== null &&
      'is_master' in variant &&
      variant.is_master === true,
  );
  if (!master) return null;

  const price = Number(master.price);
  return Number.isFinite(price) && price >= 0 ? price : null;
}

/**
 * Everywhere prices come from the store, not the catalog module, so each
 * environment's build prices what that environment actually sells. Tests the
 * store does not carry are dropped rather than shown at a stale price.
 */
export function useEverywherePricing(): EverywherePricing {
  const data = (useStaticQuery(graphql`
    query EverywherePrices {
      allSpreeProduct {
        nodes {
          slug
          variants {
            price
            is_master
          }
        }
      }
    }
  `) ?? {}) as { allSpreeProduct?: { nodes?: unknown[] } };

  const nodes = data.allSpreeProduct?.nodes;

  return useMemo(() => {
    const priceBySlug = new Map<string, number>();

    for (const node of nodes ?? []) {
      if (typeof node !== 'object' || node === null || !('slug' in node)) {
        continue;
      }

      const { slug } = node as PriceNode;
      if (typeof slug !== 'string' || !slug.startsWith('everywhere-')) continue;

      const price = masterPrice(node as PriceNode);
      if (price !== null) priceBySlug.set(slug, price);
    }

    const reviewFee = priceBySlug.get(EVERYWHERE_REVIEW_FEE_SLUG) ?? null;
    const labPriceFor = (test: EverywhereTest) =>
      priceBySlug.get(everywhereProductSlug(test)) ?? null;

    return {
      labPriceFor,
      reviewFee,
      availableTests:
        reviewFee === null
          ? []
          : TESTS.filter((test) => labPriceFor(test) !== null),
    };
  }, [nodes]);
}
